import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-base font-medium',
    'transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:shadow-focus',
    'disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-sunken disabled:text-text-disabled',
    'aria-disabled:cursor-not-allowed aria-disabled:border-border aria-disabled:bg-surface-sunken aria-disabled:text-text-disabled',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-on hover:bg-primary-hover active:bg-primary-active',
        secondary: 'border-border-strong bg-surface text-text hover:bg-surface-sunken active:bg-border',
        ghost: 'bg-transparent text-text-secondary hover:bg-surface-sunken hover:text-text active:bg-border',
        destructive: 'bg-error text-primary-on hover:bg-error-hover active:bg-error-active',
        link: 'h-auto border-0 bg-transparent p-0 text-primary underline-offset-4 hover:underline',
        // Legacy aliases kept so unmigrated call sites compile. Prefer the names above.
        default: 'bg-primary text-primary-on hover:bg-primary-hover active:bg-primary-active',
        outline: 'border-border-strong bg-surface text-text hover:bg-surface-sunken active:bg-border',
      },
      size: {
        // Height follows --control-h, which density and coarse-pointer media queries already adjust.
        default: 'h-control px-4',
        compact: 'h-[var(--control-h-compact)] px-3',
        touch: 'h-[var(--control-h-touch)] px-4',
        icon: 'h-control w-control p-0',
        // Legacy aliases.
        sm: 'h-[var(--control-h-compact)] px-3',
        lg: 'h-[var(--control-h-touch)] px-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Shows a 16px spinner, keeps the label and width, and blocks clicks. */
  loading?: boolean
  leadingIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, leadingIcon, children, disabled, type, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className)
    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {children}
        </Slot>
      )
    }
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : leadingIcon}
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
